import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { CheckCircle2 } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { CardSetupForm } from '../components/CardSetupForm.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

// "Set up card billing" - the link a billing admin sends a client who
// signed for block hours before card billing existed. No login: the token
// in the link is the capability, as with a quote. The client saves a card;
// monthly billing through Stripe starts on the next 1st.

interface SetupView {
  organizationName: string;
  monthlyHours: number;
  monthlyPriceCents: number;
  firstChargeOn: string;
  complete: boolean;
}

const money = (cents: number): string =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const longDate = (iso: string): string =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

export function BillingSetupPage(): React.ReactElement {
  useDocumentTitle('Set up card billing');
  const { token = '' } = useParams<{ token: string }>();
  const [setup, setSetup] = useState<SetupView | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ setup: SetupView }>(`/api/billing-setup/${token}`);
      setSetup(data.setup);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setNotFound(true);
      else setError('We could not load this page. Please try again in a moment.');
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="min-h-screen bg-[#F2F6F7] px-4 py-10 sm:py-16" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="mx-auto w-full max-w-[560px]">
        <div className="mb-8 flex justify-center">
          <img src="/t3-logo-nav.png" alt="T3 Tech Partners" className="h-10" />
        </div>
        <div className="rounded-[8px] border border-[rgba(129,162,178,0.25)] bg-white p-6 sm:p-8">
          {notFound ? (
            <>
              <h1 className="text-xl font-bold text-[#47535D]">This link is not valid</h1>
              <p className="mt-2 text-sm text-[#47535D]">
                It may have expired or been mistyped. Reply to the email it came in and we will send a fresh one.
              </p>
            </>
          ) : error ? (
            <p className="text-sm text-[var(--color-danger)]">{error}</p>
          ) : !setup ? (
            <p className="text-sm text-[var(--color-muted)]">Loading…</p>
          ) : setup.complete ? (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" aria-hidden="true" />
              <div>
                <h1 className="text-xl font-bold text-[#47535D]">Your card is on file - thank you</h1>
                <p className="mt-2 text-sm text-[#47535D]">
                  {money(setup.monthlyPriceCents)} for {setup.monthlyHours} hours a month will be charged on{' '}
                  {longDate(setup.firstChargeOn)}, then on the 1st of each month. You will get a receipt from Stripe each time.
                </p>
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-bold text-[#54387E]" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                Set up card billing
              </h1>
              <p className="mt-2 text-sm text-[#47535D]">
                For <strong>{setup.organizationName}</strong>: block hours, {setup.monthlyHours} hours a month.
              </p>
              <div className="mt-5">
                <CardSetupForm
                  startUrl={`/api/billing-setup/${token}/setup-intent`}
                  summary={
                    <>
                      <span className="font-semibold">Nothing is charged today.</span> {money(setup.monthlyPriceCents)} is
                      charged on {longDate(setup.firstChargeOn)}, then on the 1st of each month.
                    </>
                  }
                  buttonLabel="Save card"
                  onSaved={async (setupIntentId) => {
                    const done = await apiFetch<{ setup: SetupView }>(`/api/billing-setup/${token}/complete`, {
                      method: 'POST',
                      json: { setupIntentId },
                    });
                    setSetup(done.setup);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
